import React, { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Lock, User, Mail } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      login(response.data.access_token, response.data.user);
      toast.success('Registration successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] mb-8 transition-colors duration-300"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </button>

        <div className="mb-8">
          <h1 className="font-heading text-4xl font-semibold text-[#D4AF37] tracking-tight mb-2" data-testid="admin-login-logo">
            NOK Admin
          </h1>
          <p className="text-[#94A3B8] text-sm">Manage your property listings and content</p>
        </div>

        <div className="bg-[#131A38] border border-white/8 rounded-2xl p-8">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#0A0E27] rounded-xl">
              <TabsTrigger value="login" data-testid="login-tab" className="rounded-lg data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0E27]">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="rounded-lg data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0E27]">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-username" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                    <Input
                      id="login-username"
                      type="text"
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      required
                      className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="login-username-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full text-sm transition-colors duration-300"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="register-username" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                    <Input
                      id="register-username"
                      type="text"
                      value={registerData.username}
                      onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                      required
                      className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="register-username-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                    <Input
                      id="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                    <Input
                      id="register-password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full text-sm transition-colors duration-300"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
